/* eslint-disable max-len */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import styled from '@emotion/styled';
import moment from 'moment';
import IcomoonReact from 'icomoon-react';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import {
  setIsPrimaryAnimatedSideBarOpen, setPrimarySideBarDataTag, setSecondarySideBarDataTag, setPreviewDate, setPreviewDayNumber,
} from '../../ducks/calendarActions';
import { ICON_RAIL_WIDTH, SIDEBAR_DRAWER_BREAKPOINT } from '../../constants/sidebarDimensions';

const SideBarWrapper = styled('div')`
  background-color: white;
  width: ${ICON_RAIL_WIDTH}px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0px 4px 8px 0px #3031331A;
  z-index: 100;

  ${(props) => props.viewType === 'weekView' && 'position: fixed;'}
  ${(props) => props.viewType === 'weekView' && 'left: 0;'}
  ${(props) => props.viewType === 'weekView' && 'height: 100%;'}

  /* Small screens: the rail is hidden until the header hamburger opens the
     drawer, so the calendar gets the full viewport width. */
  @media screen and (max-width: ${SIDEBAR_DRAWER_BREAKPOINT}px) {
    ${(props) => {
    if (!props.isPrimaryAnimatedSideBarOpen) {
      return 'display: none;';
    }
    /** Week view scrolls the document horizontally, so the rail has to stay
     * `fixed` - the weekView rules above already do that. Making it absolute
     * would anchor it to the fit-content wrapper (wider than the viewport) and
     * it would slide off-screen as the page scrolls. */
    if (props.viewType === 'weekView') {
      return '';
    }
    /** Every other view scrolls inside its own container, so absolute against
     * the relative IconSideBarAndContentWrapper lifts the rail out of flow
     * without shrinking the calendar. */
    return `
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      z-index: 101;
    `;
  }}
  }
`;

const IconToggleBox = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 48px;
  max-width: 48px;
  min-height: 40px;
  max-height: 40px;
  border-right: ${(props) => (props.activeToggle ? '2px solid #444444' : '2px solid white')};
  cursor: pointer;
  transition: all 250ms ease-in-out;
  :hover {
    opacity: 0.7;
  }
`;

/** Per-icon rail sizes. `phone` reads small at the default, `gs-chart` reads
 *  heavy; everything else uses DEFAULT_ICON_SIZE. */
const DEFAULT_ICON_SIZE = 20;
const ICON_SIZES = {
  phone: 24,
  'gs-chart': 18,
};

const IconSideBar = () => {
  const dispatch = useDispatch();
  const { buildUndo } = useFlags();
  const isPrimaryAnimatedSideBarOpen = useSelector((state) => state.calendar.data.isPrimaryAnimatedSideBarOpen);
  const primarySideBarDataTag = useSelector((state) => state.calendar.data.primarySideBarDataTag);
  const programType = useSelector((state) => state.calendar.data.programType);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const weekViewExpandedDay = useSelector((state) => state.calendar.data.weekViewExpandedDay);
  const monthSelectionActiveDate = useSelector(
    (state) => state.calendar.data.monthSelectionActiveDate,
  );
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const iconArray = programType === 'free-form-program'
    ? ['note', 'lift', 'phone', 'gs-chart']
    : ['calendar2', 'lift', 'templates', 'journal', 'phone', 'gs-chart', ...(buildUndo ? ['history'] : [])];

  useEffect(() => {
    if (!isPrimaryAnimatedSideBarOpen) {
      dispatch(setPrimarySideBarDataTag(null));
      dispatch(setSecondarySideBarDataTag(null));
    }
  }, [isPrimaryAnimatedSideBarOpen, dispatch]);

  return (
    <SideBarWrapper
      viewType={viewType}
      isPrimaryAnimatedSideBarOpen={isPrimaryAnimatedSideBarOpen}
    >
      {iconArray.map((icon) => (
        <IconToggleBox
          style={{ marginTop: icon === 'phone' && 10 }}
          onClick={() => {
            // Opening the phone preview from the tab starts on the day the user is focused on
            // (not today): week view → expanded day, month view → active day, otherwise the
            // calendar's selected date. Only re-applied when switching to the phone tab.
            if (icon === 'phone' && primarySideBarDataTag !== icon) {
              if (programType === 'free-form-program') {
                // Week view: prefer the expanded program day; otherwise snapshot the currently
                // selected program day. We write a concrete value (not null) so the preview stays
                // independent of later calendar navigation.
                if (viewType === 'weekView' && weekViewExpandedDay != null) {
                  dispatch(setPreviewDayNumber(weekViewExpandedDay));
                } else {
                  dispatch(setPreviewDayNumber(selectedProgramDayNumber ?? 0));
                }
              } else {
                let defaultDate;
                if (viewType === 'weekView' && weekViewExpandedDay) {
                  defaultDate = weekViewExpandedDay; // YYYY-MM-DD dayKey
                } else if (viewType === 'monthView' && monthSelectionActiveDate) {
                  defaultDate = monthSelectionActiveDate; // YYYY-MM-DD
                } else {
                  defaultDate = moment(selectedDate).format('YYYY-MM-DD');
                }
                dispatch(setPreviewDate(defaultDate));
              }
            }
            dispatch(setIsPrimaryAnimatedSideBarOpen(primarySideBarDataTag !== icon));
            /** set timeout to wait for transition then set datatag */
            if (primarySideBarDataTag !== icon && !isPrimaryAnimatedSideBarOpen) {
              setTimeout(() => {
                dispatch(setPrimarySideBarDataTag(icon));
              }, 250);
            } else {
              dispatch(setPrimarySideBarDataTag(icon));
            }
          }}
          activeToggle={primarySideBarDataTag === icon}
        >
          <IcomoonReact
            iconSet={iconSet}
            size={ICON_SIZES[icon] ?? DEFAULT_ICON_SIZE}
            icon={icon}
            color={primarySideBarDataTag === icon ? '#444444' : '#9E9E9E'}
          />
        </IconToggleBox>
      ))}
    </SideBarWrapper>
  );
};

export default IconSideBar;
